import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { toApiError } from "@/lib/api-error";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { PageHeader } from "@/components/v2/PageHeader";
import { StatCard } from "@/components/v2/StatCard";
import {
  DateRangePicker,
  defaultRange,
  toIsoDate,
  type DateRange,
} from "@/components/v2/DateRangePicker";
import { Banner } from "@/components/v2/Banner";
import { TrendChart } from "@/components/v2/insights/TrendChart";
import { StatusDonut } from "@/components/v2/insights/StatusDonut";
import { TopServicesList } from "@/components/v2/insights/TopServicesList";

/**
 * Insights — Pencil frame `NeqTB`. Executive dashboard for the operator's
 * tenant: hero KPIs, daily trend, status mix, top services, and operational
 * secondaries — all with period-over-period deltas vs the prior window.
 *
 * Layout contract: each row is its own CSS-grid; cards inside a row share
 * the row's height (every StatCard uses `flex flex-col h-full`).
 */


type DayPoint = {
  date?: string;
  bookings?: number;
  revenue?: number | string;
};

const num = (v: unknown): number =>
  typeof v === "number"
    ? v
    : typeof v === "string"
      ? Number(v) || 0
      : 0;

/** Period-over-period delta as a fraction (0.12 = +12%). Returns 0 if the
 *  prior period was zero (no division-by-zero spikes). */
const deltaPct = (current: number, prior: number): number => {
  if (prior === 0) return 0;
  return (current - prior) / prior;
};

/** Percentage-point delta — for ratios already in the 0..1 range. */
const deltaPp = (current: number, prior: number): number =>
  (current - prior) * 100;

export function Insights() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useTenantMe();
  const tenantCurrency = tenant.data?.tenant.currency ?? "PHP";
  /** Closes over the tenant currency so KPI cards format in the active
   *  workspace's currency. Stays compatible with callers that pass an
   *  explicit per-row currency (e.g., a row stamped at booking time). */
  const fmtMoney = (n: number, currency = tenantCurrency) =>
    formatMoney(currency, n, { fractionDigits: 0 });
  const [range, setRange] = useState<DateRange>(() => defaultRange());
  const fromIso = toIsoDate(range.from);
  const toIso = toIsoDate(range.to);

  const summary = useQuery({
    queryKey: ["insights", fromIso, toIso],
    enabled: !!accessToken,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/insights", {
        params: { query: { from: fromIso, to: toIso } },
      });
      if (error) throw toApiError(error);
      return data;
    },
  });

  const data = summary.data;

  // --- derived metrics (frontend-only) ---
  const derived = useMemo(() => {
    if (!data) return null;
    const totalBookings = num(data.totalBookings);
    const totalRevenue = num(data.totalRevenue);
    const sb = data.statusBreakdown ?? {};
    const psb = data.previous?.statusBreakdown ?? {};

    const confirmed = num(sb.confirmed) + num(sb.completed);
    const cancelled = num(sb.cancelled) + num(sb.canceled);
    const noShow = num(sb.no_show);

    const prevTotal = num(data.previous?.totalBookings);
    const prevConfirmed = num(psb.confirmed) + num(psb.completed);
    const prevCancelled = num(psb.cancelled) + num(psb.canceled);
    const prevNoShow = num(psb.no_show);

    const conversion = totalBookings ? confirmed / totalBookings : 0;
    const prevConversion = prevTotal ? prevConfirmed / prevTotal : 0;
    const aov = totalBookings ? totalRevenue / totalBookings : 0;
    const prevAov = prevTotal
      ? num(data.previous?.totalRevenue) / prevTotal
      : 0;
    const cancellation = totalBookings ? cancelled / totalBookings : 0;
    const prevCancellation = prevTotal ? prevCancelled / prevTotal : 0;
    const noShowRate = totalBookings ? noShow / totalBookings : 0;
    const prevNoShowRate = prevTotal ? prevNoShow / prevTotal : 0;

    // Sparkline series for the hero cards — last N days of bookings/revenue.
    const trendBookings = (data.byDay ?? []).map((d: DayPoint) =>
      num(d.bookings),
    );
    const trendRevenue = (data.byDay ?? []).map((d: DayPoint) => num(d.revenue));

    return {
      totalBookings,
      totalRevenue,
      conversion,
      aov,
      cancellation,
      noShowRate,
      deltas: {
        bookings: deltaPct(totalBookings, prevTotal),
        revenue: deltaPct(totalRevenue, num(data.previous?.totalRevenue)),
        conversionPp: deltaPp(conversion, prevConversion),
        aov: deltaPct(aov, prevAov),
        cancellationPp: deltaPp(cancellation, prevCancellation),
        noShowPp: deltaPp(noShowRate, prevNoShowRate),
        repeatPp: deltaPp(
          num(data.repeatCustomerRate),
          num(data.previous?.repeatCustomerRate),
        ),
      },
      trendBookings,
      trendRevenue,
    };
  }, [data]);

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Insights"
        subtitle="Trends over the picked window · vs prior period"
        actions={
          <DateRangePicker value={range} onChange={setRange} align="end" />
        }
      />

      {summary.isLoading && <SkeletonGrid />}
      {summary.isError && (
        <Banner
          tone="error"
          title="Couldn't load insights"
          description={(summary.error as Error).message}
        />
      )}

      {data && derived && (
        <>
          {/* ROW 1 — hero KPIs */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total bookings"
              value={derived.totalBookings.toLocaleString()}
              delta={{
                value: derived.deltas.bookings,
                label: `vs prior period`,
              }}
              trend={derived.trendBookings}
            />
            <StatCard
              label="Total revenue"
              value={fmtMoney(derived.totalRevenue)}
              delta={{
                value: derived.deltas.revenue,
                label: `vs prior period`,
              }}
              trend={derived.trendRevenue}
            />
            <StatCard
              label="Conversion rate"
              value={`${Math.round(derived.conversion * 100)}%`}
              hint="Confirmed + completed / total"
              delta={{
                value: derived.deltas.conversionPp,
                suffix: "pp",
                label: `vs prior period`,
              }}
            />
            <StatCard
              label="Avg booking value"
              value={fmtMoney(derived.aov)}
              hint="Revenue / total bookings"
              delta={{
                value: derived.deltas.aov,
                label: `vs prior period`,
              }}
            />
          </div>

          {/* ROW 2 — trend chart */}
          <TrendChart
            byDay={(data.byDay ?? []) as DayPoint[]}
            currency={tenantCurrency}
          />

          {/* ROW 3 — status breakdown + top services */}
          <div className="grid gap-4 lg:grid-cols-2">
            <StatusDonut breakdown={data.statusBreakdown ?? {}} />
            <TopServicesList
              services={data.topServices ?? []}
              currency={tenantCurrency}
            />
          </div>

          {/* ROW 4 — operational secondaries */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Peak day"
              value={
                data.peakDay
                  ? new Date(data.peakDay.date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })
                  : "—"
              }
              hint={
                data.peakDay
                  ? `${data.peakDay.bookings} booking${
                      data.peakDay.bookings === 1 ? "" : "s"
                    } · ${fmtMoney(num(data.peakDay.revenue))}`
                  : "No bookings in this window."
              }
            />
            <StatCard
              label="Cancellation rate"
              value={`${Math.round(derived.cancellation * 100)}%`}
              hint="Cancelled / total"
              delta={{
                value: derived.deltas.cancellationPp,
                suffix: "pp",
                label: `vs prior period`,
                invertGoodBad: true,
              }}
            />
            <StatCard
              label="No-show rate"
              value={`${Math.round(derived.noShowRate * 100)}%`}
              hint="No-show / total"
              delta={{
                value: derived.deltas.noShowPp,
                suffix: "pp",
                label: `vs prior period`,
                invertGoodBad: true,
              }}
            />
            <StatCard
              label="Repeat-customer rate"
              value={`${Math.round(num(data.repeatCustomerRate) * 100)}%`}
              hint="Customers w/ >1 booking"
              delta={{
                value: derived.deltas.repeatPp,
                suffix: "pp",
                label: `vs prior period`,
              }}
            />
          </div>

          {/* ROW 5 — pipeline strip */}
          <section className="rounded-xl border bg-card p-5 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pipeline value</p>
                <p className="text-2xl font-semibold tracking-tight tabular-nums">
                  {fmtMoney(num(data.pipelineRevenue))}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarClock className="h-4 w-4" />
              <span>
                <span className="text-foreground font-medium tabular-nums">
                  {num(data.pipelineBookings)}
                </span>{" "}
                confirmed booking
                {num(data.pipelineBookings) === 1 ? "" : "s"} starting after
                today
              </span>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/** Layout-matching skeleton so the loading → loaded transition doesn't shift. */
function SkeletonGrid() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border bg-card p-5 h-32" />
        ))}
      </div>
      <div className="rounded-xl border bg-card p-5 h-80" />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-5 h-72" />
        <div className="rounded-xl border bg-card p-5 h-72" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border bg-card p-5 h-28" />
        ))}
      </div>
      <div className="rounded-xl border bg-card p-5 h-20" />
    </div>
  );
}
